"use client";

/**
 * Client body for the AFAS Profit integration page. Lives apart from
 * `page.tsx` (which stays a server component for `metadata`) so it can call
 * `useT()` and follow the visitor's locale, exactly like the Freshdesk page.
 * All copy comes from the `intgAfas` namespace; structure (icons, hrefs,
 * capability status) stays here.
 */

import {
  Lock,
  ListChecks,
  EyeOff,
  ShieldCheck,
  Activity,
  UserCheck,
  Scale,
} from "lucide-react";
import { MarketingShell } from "@/components/marketing/marketing-shell";
import {
  UseCaseHero,
  Section,
  SectionHeader,
  FeatureGrid,
  CapabilityGrid,
  Faq,
  SecurityNote,
  RelatedLinks,
  CtaBanner,
} from "@/components/marketing/marketing-kit";
import { useT } from "@/i18n";

export function AfasContent() {
  const t = useT();
  const k = (s: string) => `intgAfas.${s}`;

  const features = [
    { key: "readOnly", icon: <Lock size={18} /> },
    { key: "namedViews", icon: <ListChecks size={18} /> },
    { key: "projection", icon: <EyeOff size={18} /> },
    { key: "credentials", icon: <ShieldCheck size={18} /> },
    { key: "health", icon: <Activity size={18} /> },
    { key: "governedAnswer", icon: <UserCheck size={18} /> },
  ].map((f) => ({
    icon: f.icon,
    title: t(k(`features.${f.key}.title`)),
    body: t(k(`features.${f.key}.body`)),
  }));

  const capabilities = [
    { key: "leaveBalance", status: "conditional" as const },
    { key: "payrollCode", status: "conditional" as const },
    { key: "projection", status: "supported" as const },
    { key: "connectionCheck", status: "supported" as const },
    { key: "writeBack", status: "unavailable" as const },
    { key: "salaryAmounts", status: "unavailable" as const },
  ].map((c) => ({
    name: t(k(`capabilities.${c.key}.name`)),
    status: c.status,
    body: t(k(`capabilities.${c.key}.body`)),
  }));

  const faq = ["q1", "q2", "q3", "q4", "q5", "q6"].map((q) => ({
    q: t(k(`faq.${q}.q`)),
    a: t(k(`faq.${q}.a`)),
  }));

  return (
    <MarketingShell>
      <UseCaseHero
        eyebrow={t(k("hero.eyebrow"))}
        title={t(k("hero.title"))}
        highlight={t(k("hero.highlight"))}
        subtitle={t(k("hero.subtitle"))}
        primary={{ href: "/demo", label: t(k("hero.primaryCta")) }}
        secondary={{ href: "/use-cases/hr-governed-answers", label: t(k("hero.secondaryCta")) }}
        bullets={[t(k("hero.bullet1")), t(k("hero.bullet2")), t(k("hero.bullet3"))]}
      />

      <Section>
        <SectionHeader eyebrow={t(k("what.eyebrow"))} title={t(k("what.title"))} />
        <FeatureGrid features={features} />
      </Section>

      <Section muted>
        <SectionHeader
          eyebrow={t(k("capabilities.eyebrow"))}
          title={t(k("capabilities.title"))}
          description={t(k("capabilities.description"))}
        />
        <CapabilityGrid
          capabilities={capabilities}
          statusLabels={{
            supported: t("mk.cap.supported"),
            conditional: t("mk.cap.conditional"),
            unavailable: t("mk.cap.unavailable"),
          }}
        />
      </Section>

      <Section>
        <SectionHeader eyebrow={t(k("governed.eyebrow"))} title={t(k("governed.title"))} />
        <SecurityNote icon={<Scale size={18} />} title={t(k("governed.noteTitle"))}>
          {t(k("governed.noteBody"))}
        </SecurityNote>
      </Section>

      <Section muted>
        <SectionHeader eyebrow={t(k("faq.eyebrow"))} title={t(k("faq.title"))} />
        <Faq items={faq} />
      </Section>

      <Section>
        <RelatedLinks
          title={t("mk.relatedTitle")}
          links={[
            {
              href: "/use-cases/hr-governed-answers",
              label: t(k("related.hrGovernance.label")),
              description: t(k("related.hrGovernance.description")),
            },
            {
              href: "/integrations/youforce",
              label: t(k("related.youforce.label")),
              description: t(k("related.youforce.description")),
            },
            {
              href: "/use-cases/ai-agent-approval-ledger",
              label: t(k("related.approvalLedger.label")),
              description: t(k("related.approvalLedger.description")),
            },
          ]}
        />
      </Section>

      <CtaBanner
        title={t(k("cta.title"))}
        description={t(k("cta.description"))}
        primary={{ href: "/demo", label: t(k("cta.primary")) }}
        secondary={{ href: "/contact", label: t(k("cta.secondary")) }}
      />
    </MarketingShell>
  );
}
